import { useEffect, useState } from "react";

import { format } from "date-fns";

import { useEventEmitter } from "../useEventEmitter";

export interface UseDateRange {
  closePopoverEvent: ReturnType<typeof useEventEmitter<void>>;
  currentMonth: Date;
  tempRange: {
    start: Date | null;
    end: Date | null;
  };
  handleDateClick: (date: Date) => void;
  handleApply: () => void;
  handleCancel: () => void;
  formatRangeDisplay: () => string;
  setCurrentMonth: (date: Date) => void;
}

interface Props {
  closePopoverEvent?: ReturnType<typeof useEventEmitter<void>>;
  placeholder: string;
  value?: { start: string | null; end: string | null };
  onChange?: (
    startDate: string,
    endDate: string,
    reportingYearId?: string
  ) => void;
}

interface DateSpan {
  start: Date | null;
  end: Date | null;
}

export const useDateRange = (props: Props): UseDateRange => {
  const { value = { start: null, end: null }, onChange, placeholder } = props;

  const convertToDateSpan = (value: {
    start: string | null;
    end: string | null;
  }): DateSpan => {
    return {
      start: value.start ? new Date(value.start) : null,
      end: value.end ? new Date(value.end) : null,
    };
  };

  const [tempRange, setTempRange] = useState<DateSpan>(
    convertToDateSpan(value)
  );
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    setTempRange(convertToDateSpan({ start: value.start, end: value.end }));
    setCurrentMonth(new Date(value.end || value.start || new Date()));
  }, [value.start, value.end]);

  const closePopoverEvent = useEventEmitter<void>(props.closePopoverEvent);

  const handleApply = () => {
    onChange?.(
      tempRange.start ? format(tempRange.start, "yyyy-MM-dd") : "",
      tempRange.end ? format(tempRange.end, "yyyy-MM-dd") : ""
    );
    closePopoverEvent.emit();
  };

  const handleCancel = () => {
    setTempRange(convertToDateSpan(value));
    closePopoverEvent.emit();
  };

  const handleDateClick = (date: Date) => {
    if (!tempRange.start || (tempRange.start && tempRange.end)) {
      // Start new selection
      setTempRange({ start: date, end: null });
    } else {
      // Complete the range
      if (date < tempRange.start) {
        setTempRange({ start: date, end: tempRange.start });
      } else {
        setTempRange({ start: tempRange.start, end: date });
      }
    }
  };

  const formatRangeDisplay = (): string => {
    if (!value.start && !value.end) {
      return placeholder;
    }

    const dateSpan = convertToDateSpan(value);
    if (dateSpan.start && dateSpan.end) {
      return `${format(dateSpan.start, "MMM dd, yyyy")} - ${format(
        dateSpan.end,
        "MMM dd, yyyy"
      )}`;
    }
    if (dateSpan.start) {
      return `${format(dateSpan.start, "MMM dd, yyyy")}`;
    }
    return placeholder;
  };

  return {
    closePopoverEvent,
    currentMonth,
    tempRange,
    handleDateClick,
    handleApply,
    handleCancel,
    formatRangeDisplay,
    setCurrentMonth,
  };
};
